// Tipos de `painel_evento` (espelho de `core/sse.py`).
export const EVENTO_ABERTURA = 'abertura'
export const EVENTO_STATUS = 'status'
export const EVENTO_PARECER = 'parecer'
export const EVENTO_RODADA = 'rodada'
export const EVENTO_ENCERRAMENTO = 'encerramento'
// A fala do especialista, chegando token a token enquanto ele escreve (P10).
export const EVENTO_FALA = 'fala'
// O raciocínio que ESCOLHEU a mesa — o primeiro bloco da sala, e o texto que
// preenche o "Raciocínio da IA" (G11). Não é `thinking` do provider: a Anthropic
// não expõe reasoning por este caminho, e o bloco nascia vazio por isso.
export const EVENTO_TRIAGEM = 'triagem'

// Estados de uma área na sala. `aguardando` é só do frontend: existe entre a
// abertura (que já monta os cards) e o primeiro `status` do backend.
export const ESTADO_AGUARDANDO = 'aguardando'
export const ESTADO_ANALISANDO = 'analisando'
export const ESTADO_CONCLUIDO = 'concluido'
export const ESTADO_INDISPONIVEL = 'indisponivel'

export const MODO_PAINEL = 'painel'
export const MODO_REVISAO_DE_ANALISE = 'revisao_de_analise'

// Corte do trecho citado na réplica. Vive aqui, e não no template, porque o
// template não chama função — e porque é decisão de layout, não do backend
// (que manda o texto integral, ver `_quote_da_contraparte`).
export const LIMITE_DO_QUOTE = 180

// A réplica que muda o resultado é a que interessa ler antes; `mantenho` é o
// default de quem não respondeu e vai por último. Espelho de
// `transcricao._turnos_rodada_2` — a ordem TEM de ser a mesma, senão a sala ao
// vivo e a sala reaberta mostram o debate em ordens diferentes.
const ORDEM_DA_POSICAO = { cedo: 0, nao_alcanca: 1, refino: 2, mantenho: 3 }
const ORDEM_DA_POSICAO_DESCONHECIDA = 9

/**
 * Compara duas strings por code point, como o `sorted` do Python.
 *
 * `localeCompare` ordenaria "Ática" antes de "Alfa" (acento ignorado) e a
 * ordem do frontend divergiria da do backend justamente nos rótulos acentuados.
 *
 * @param {String} a
 * @param {String} b
 * @returns {Number}
 */
function compararTexto(a, b) {
  if (a === b) return 0
  return a < b ? -1 : 1
}

/**
 * Rodada 1 na ordem do backend: o lead abre a mesa, os demais por rótulo.
 *
 * Os eventos chegam na ordem em que cada área TERMINA (`as_completed`) — ruído
 * de concorrência. Reaplicar aqui o mesmo critério de `_turnos_rodada_1` é o que
 * garante a paridade: para qualquer ordem de chegada, a sala ao vivo converge
 * para exatamente a lista que a transcrição final traz.
 *
 * @param {Array} turnos Turnos de rodada 1, na ordem de chegada.
 * @returns {Array} Nova lista ordenada.
 */
function ordenarRodada1(turnos) {
  return turnos.slice().sort(function(a, b) {
    if (Boolean(a.lead) !== Boolean(b.lead)) return a.lead ? -1 : 1
    return compararTexto(a.label || '', b.label || '')
  })
}

/**
 * Rodada 2 na ordem do backend: quem resolveu o conflito antes de quem manteve.
 *
 * @param {Array} turnos Turnos de rodada 2, na ordem de chegada.
 * @returns {Array} Nova lista ordenada (ordenação estável, como a do Python).
 */
function ordenarRodada2(turnos) {
  return turnos.slice().sort(function(a, b) {
    const ordemA = posicaoNaOrdem(a.posicao)
    const ordemB = posicaoNaOrdem(b.posicao)
    if (ordemA !== ordemB) return ordemA - ordemB
    return compararTexto(a.label || '', b.label || '')
  })
}

function posicaoNaOrdem(posicao) {
  if (posicao && Object.hasOwn(ORDEM_DA_POSICAO, posicao)) return ORDEM_DA_POSICAO[posicao]
  return ORDEM_DA_POSICAO_DESCONHECIDA
}

function turnosDaRodada(turnos, rodada) {
  return turnos.filter(function(t) { return t.rodada === rodada })
}

/**
 * Os turnos na ordem canônica da transcrição: rodada 1, depois 2, depois 3.
 *
 * Turno de rodada desconhecida vai para o fim, preservado — descartá-lo
 * esconderia do gestor uma fala que aconteceu.
 *
 * @param {Array} turnos Turnos na ordem de chegada.
 * @returns {Array}
 */
function ordenarTurnos(turnos) {
  const conhecidas = new Set([1, 2, 3])
  const outros = turnos.filter(function(t) { return !conhecidas.has(t.rodada) })
  return ordenarRodada1(turnosDaRodada(turnos, 1))
    .concat(ordenarRodada2(turnosDaRodada(turnos, 2)))
    .concat(turnosDaRodada(turnos, 3))
    .concat(outros)
}

function aplicarAbertura(acumulado, evento) {
  acumulado.assunto = evento.assunto || ''
  acumulado.modo = evento.modo || MODO_PAINEL
  acumulado.mesa = evento.mesa || []
  for (const cadeira of acumulado.mesa) {
    if (cadeira && cadeira.area) acumulado.estadoPorArea[cadeira.area] = ESTADO_AGUARDANDO
  }
}

/**
 * O racional da triage. Substitui, não concatena: a triage roda uma vez por
 * turno, e um segundo evento só chegaria por replay de reconnect — acumular
 * duplicaria o texto na tela.
 *
 * ⚠️ Racional de rastro (bypass do Consultor, modelo indisponível) já foi
 * filtrado no backend por `triage.racional_publico`, que é dono ÚNICO da regra
 * justamente para o vivo e o reaberto concordarem. Não refiltrar aqui: uma
 * segunda regra divergiria na primeira mudança.
 */
function aplicarTriagem(acumulado, evento) {
  if (!evento.racional) return
  acumulado.racional = evento.racional
}

function aplicarStatus(acumulado, evento) {
  // Heartbeat só mantém a conexão viva enquanto a tool demora — não é estado de
  // ninguém, e tratá-lo como tal apagaria o estado real da última área.
  if (evento.heartbeat) return
  if (!evento.area || !evento.estado) return
  acumulado.estadoPorArea[evento.area] = evento.estado
  if (evento.motivo) acumulado.motivoPorArea[evento.area] = evento.motivo
}

/**
 * A chave do acumulador de fala: uma por (área, rodada).
 *
 * A mesma área fala duas vezes no painel — parecer na rodada 1, réplica na 2.
 * Chavear só por área grudaria os dois num parágrafo que ninguém disse.
 *
 * @param {String} area
 * @param {Number} rodada
 * @returns {String}
 */
function chaveDaFala(area, rodada) {
  return area + '|' + rodada
}

function aplicarFala(acumulado, evento) {
  if (!evento.area) return
  const rodada = typeof evento.rodada === 'number' ? evento.rodada : 1
  const chave = chaveDaFala(evento.area, rodada)
  const atual = Object.hasOwn(acumulado.falas, chave)
    ? acumulado.falas[chave]
    : { area: evento.area, rodada: rodada, texto: '', fim: false }
  acumulado.falas[chave] = {
    area: atual.area,
    rodada: atual.rodada,
    texto: atual.texto + (evento.delta || ''),
    fim: Boolean(evento.fim),
  }
}

/**
 * A fala acumulada de uma área numa rodada — `''` quando não houve stream.
 *
 * @param {Object} painel Parcial de `reduzirEventos`.
 * @param {String} area
 * @param {Number} rodada
 * @returns {String}
 */
export function falaDe(painel, area, rodada) {
  const falas = (painel && painel.falas) || {}
  const chave = chaveDaFala(area, rodada)
  if (!Object.hasOwn(falas, chave)) return ''
  return falas[chave].texto
}

/**
 * Se a área ainda está escrevendo esta fala (recebeu delta, não recebeu `fim`).
 *
 * @param {Object} painel Parcial de `reduzirEventos`.
 * @param {String} area
 * @param {Number} rodada
 * @returns {Boolean}
 */
export function falandoAgora(painel, area, rodada) {
  const falas = (painel && painel.falas) || {}
  const chave = chaveDaFala(area, rodada)
  if (!Object.hasOwn(falas, chave)) return false
  return !falas[chave].fim
}

function aplicarCusto(acumulado, custo) {
  if (!custo || typeof custo.custo_brl !== 'number') return
  acumulado.custo_brl = custo.custo_brl
  acumulado.custo_aproximado = Boolean(custo.aproximado)
}

function aplicarEncerramento(acumulado, evento) {
  acumulado.encerramento = evento.encerramento || ''
  if (typeof evento.rodadas === 'number') acumulado.rodadas = evento.rodadas
  if (typeof evento.duracao_s === 'number') acumulado.duracao_s = evento.duracao_s
  aplicarCusto(acumulado, evento.custo)
  acumulado.parecer = evento.parecer || null
}

// O parecer não escreve no acumulado: ele alimenta a lista que `ordenarTurnos`
// reordena no fim, então recebe `turnos` como terceiro argumento — a assinatura
// que a tabela usa para todos.
function aplicarParecer(acumulado, evento, turnos) {
  if (evento.turno) turnos.push(evento.turno)
}

function aplicarRodada(acumulado, evento) {
  if (typeof evento.rodada === 'number') acumulado.rodadas = evento.rodada
  aplicarCusto(acumulado, evento.custo)
}

// Tabela de despacho e não a cadeia de `else if` que estava aqui: com sete
// ramos a complexidade cognitiva do `reduzirEventos` batia em 16 (limite 15),
// e cada tipo de evento novo somava mais um. Aqui o custo de um tipo novo é uma
// linha, e o laço volta a ter uma responsabilidade só.
const APLICADOR_POR_TIPO = {
  [EVENTO_ABERTURA]: aplicarAbertura,
  [EVENTO_STATUS]: aplicarStatus,
  [EVENTO_PARECER]: aplicarParecer,
  [EVENTO_FALA]: aplicarFala,
  [EVENTO_TRIAGEM]: aplicarTriagem,
  [EVENTO_RODADA]: aplicarRodada,
  [EVENTO_ENCERRAMENTO]: aplicarEncerramento,
}

/**
 * Reduz os `painel_evento` de um turno ao MESMO shape da transcrição final.
 *
 * Função pura, sem Vue: o getter do store a chama a cada evento aplicado, e o
 * componente não sabe se está lendo a sala ao vivo ou a reaberta — é o que faz
 * as duas serem idênticas por construção.
 *
 * Sobre a ordem: os eventos chegam na ordem em que cada área termina, e a
 * transcrição final ordena (lead primeiro na rodada 1; quem cedeu primeiro na
 * rodada 2). Este reducer reaplica os MESMOS critérios em vez de preservar a
 * ordem de chegada — sem isso, reabrir a conversa reembaralharia a sala, que é
 * exatamente o que a paridade de US-1 proíbe.
 *
 * Campos só-do-vivo (`aoVivo`, `estadoPorArea`, `motivoPorArea`,
 * `custo_aproximado`) não existem no payload persistido: quando o evento
 * `painel` final chega, ele substitui este parcial inteiro.
 *
 * @param {Array} eventos Eventos aplicados, na ordem de chegada.
 * @returns {Object|null} Parcial no shape da transcrição, ou `null` sem eventos.
 */
export function reduzirEventos(eventos) {
  if (!eventos || !eventos.length) return null
  const acumulado = {
    assunto: '',
    modo: MODO_PAINEL,
    mesa: [],
    rodadas: 1,
    encerramento: null,
    duracao_s: null,
    custo_brl: null,
    custo_aproximado: false,
    parecer: null,
    estadoPorArea: {},
    motivoPorArea: {},
    falas: {},
    racional: '',
  }
  const turnos = []
  for (const evento of eventos) {
    if (!evento) continue
    const aplicar = APLICADOR_POR_TIPO[evento.tipo]
    if (aplicar) aplicar(acumulado, evento, turnos)
  }
  return {
    assunto: acumulado.assunto,
    modo: acumulado.modo,
    mesa: acumulado.mesa,
    rodadas: acumulado.rodadas,
    encerramento: acumulado.encerramento,
    duracao_s: acumulado.duracao_s,
    custo_brl: acumulado.custo_brl,
    custo_aproximado: acumulado.custo_aproximado,
    parecer: acumulado.parecer,
    // Mesma chave que `transcricao.montar` grava no painel persistido
    // (`transcricao.py:292`) — é o que faz o `chat-message.racionalDoPainel` ler
    // dos dois caminhos sem saber de qual veio.
    racional: acumulado.racional,
    turnos: ordenarTurnos(turnos),
    aoVivo: true,
    estadoPorArea: acumulado.estadoPorArea,
    // Prévia do que cada área está escrevendo. NUNCA persiste: quando o turno
    // chega, o texto do artefato manda (RN-10).
    falas: acumulado.falas,
    motivoPorArea: acumulado.motivoPorArea,
  }
}

/**
 * Corta o trecho citado no limite de layout, sem cortar palavra ao meio.
 *
 * @param {String} texto Texto integral do item contestado.
 * @returns {String} Texto exibível.
 */
export function truncarQuote(texto) {
  const limpo = String(texto || '').trim()
  if (limpo.length <= LIMITE_DO_QUOTE) return limpo
  const cortado = limpo.slice(0, LIMITE_DO_QUOTE)
  const ultimoEspaco = cortado.lastIndexOf(' ')
  const base = ultimoEspaco > 0 ? cortado.slice(0, ultimoEspaco) : cortado
  return base.replace(/[.,;:—-]+$/, '') + '…'
}

/**
 * Extrai a transcrição do painel deliberativo de uma mensagem persistida.
 *
 * Mesmo mecanismo de `servicos-citados.js`, e pelo mesmo motivo: lê
 * `message.tool_calls`, acha a entrada `name === 'convocar_painel'` e devolve o
 * que o backend gravou em `painel`. Uma extração, dois caminhos — mensagem ao
 * vivo e mensagem reaberta renderizam idênticas por construção, sem coluna nova.
 *
 * Fica em `painel` (e não em `output`, como os serviços citados) porque a
 * transcrição nunca é o retorno da ferramenta: ela viaja pelo canal `ui`, que o
 * modelo não lê. Guardá-la em `output` sugeriria que o Consultor a recebeu.
 *
 * Conversas anteriores à feature não têm a chave — devolvem `null`, sem quebrar.
 *
 * @param {Object} message Mensagem no shape que o backend persiste.
 * @returns {Object|null} A transcrição, ou `null` quando o turno não teve painel.
 */
export function extrairPainel(message) {
  if (!message || !message.tool_calls || !message.tool_calls.length) return null
  for (const chamada of message.tool_calls) {
    if (chamada && chamada.name === 'convocar_painel' && chamada.painel) {
      return chamada.painel
    }
  }
  return null
}

/**
 * Agrupa os turnos por rodada, preservando a ordem em que a transcrição os traz.
 *
 * A ordenação dentro de cada rodada é decidida no backend (o lead abre a mesa;
 * quem cede aparece antes de quem mantém) — aqui só se recorta, nunca se
 * reordena. Duplicar o critério no frontend criaria duas versões que divergem
 * no primeiro ajuste.
 *
 * @param {Object} painel Transcrição vinda do backend.
 * @returns {Array} `[{ rodada, turnos }]`, sem rodadas vazias.
 */
export function agruparPorRodada(painel) {
  if (!painel || !painel.turnos || !painel.turnos.length) return []
  const grupos = []
  for (const turno of painel.turnos) {
    const ultimo = grupos.at(-1)
    if (ultimo && ultimo.rodada === turno.rodada) {
      ultimo.turnos.push(turno)
      continue
    }
    grupos.push({ rodada: turno.rodada, turnos: [turno] })
  }
  return grupos
}
